
import { Color3, Quaternion, Vector3 } from '@babylonjs/core/Maths/math';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { UvMapFeature } from '@models/classes/features/uv-map-feature';
import { Token } from '@models/classes/token';
import { ITrackedClass } from '@models/interfaces';
import { rotationToVector } from '@view/helpers';
import { SubmeshVertexData } from '@view/helpers/geometry-helper';
import { createUvMapVisualization, UVMapHelper } from '@view/helpers/uvmap-helper';
import { FeatureRenderer } from './feature';

const uvHelper = new UVMapHelper();

export class UvMapRenderer extends FeatureRenderer<UvMapFeature> {
    modelToken = Token.UvMapFeature;
    babylonUvPreviewsMap = new Map<string, Mesh>();
    previewLayer: UtilityLayerRenderer = this.viewer.utilityLayer;

    updateMeshGeometry(
        feature: UvMapFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges);

        uvHelper.generateUVMapInPlace(newVertexData, feature);

        return newVertexData;
    }

    protected updatePreviewMesh(feature: UvMapFeature) {
        let visualizationMesh = this.babylonUvPreviewsMap.get(feature.id);
        const scene = this.previewLayer.utilityLayerScene;

        if (!visualizationMesh) {
            visualizationMesh = createUvMapVisualization(feature, scene, Color3.Yellow());
            visualizationMesh.isPickable = false;
            this.babylonUvPreviewsMap.set(feature.id, visualizationMesh);
        } else {
            this.deletePreview3d(feature);
            visualizationMesh = createUvMapVisualization(feature, scene, Color3.Yellow());
            visualizationMesh.isPickable = false;
            this.babylonUvPreviewsMap.set(feature.id, visualizationMesh);
        }

        let meshPosition = visualizationMesh.getAbsolutePosition();
        if (feature._parent) {
            const featureParent = this.viewer.getRendererNode(feature._parent);
            meshPosition = featureParent.getAbsolutePosition();
        }
        meshPosition.add(feature.center.toVec3());
        meshPosition.addInPlace(feature.center.toVec3());
        visualizationMesh.position.set(meshPosition.x, meshPosition.y, meshPosition.z);
        //visualizationMesh.project;

        const newNormal = new Vector3();

        newNormal.set(feature.projectionAxis.x, feature.projectionAxis.y, feature.projectionAxis.z);

        visualizationMesh.rotationQuaternion = Quaternion.RotationAxis(newNormal, (feature.angle / 180) * Math.PI);
        visualizationMesh.rotationQuaternion = visualizationMesh.rotationQuaternion.multiply(
            rotationToVector(newNormal)
        );
        visualizationMesh.scaling.set(feature.scale.x, feature.scale.y, feature.scale.z);
    }

    update3dInternal(feature: ITrackedClass<UvMapFeature>) {
        const visualizationMesh = this.babylonUvPreviewsMap.get(feature.id);

        visualizationMesh?.setEnabled(feature._previewMaterialVisible);

        // change visibility here
        if (feature._previewMaterialVisible) {
            this.updatePreviewMesh(feature);
        }
    }

    delete3d(feature: UvMapFeature) {
        this.deletePreview3d(feature);
        super.delete3d(feature as ITrackedClass<UvMapFeature>);
    }

    deletePreview3d(feature: UvMapFeature) {
        const previewMesh = this.babylonUvPreviewsMap.get(feature.id);
        if (previewMesh) {
            previewMesh.dispose();
        }
        this.babylonUvPreviewsMap.delete(feature.id);
    }
}
